// needed for trustcomponent
declare global {
  interface Window {
    submitChallenge: any;
    request_hcaptcha_checkbox: any;
    request_hcaptcha_invisible: any;
    error_tracking: any;
    useToken: any;
  }
}

import originalPlaceholder from "@assets/images/inline-editor/original_placeholder.jpg";

// Get nested key from object, e.g. getNestedKey({a: {b: 1}}, "a.b") => 1
export function getNestedKey(obj: any, key: string) {
  const keys = key.split(".");
  let current = obj;
  for (let i = 0; i < keys.length; i++) {
    current = current[keys[i]];
  }
  return current;
}

export type PreviewImage = {
  image: string;
  width: number;
  height: number;
};

// Generate preview (base64 string) from file, can be used as src for <img>
export async function generatePreviewFromFile(file: File): Promise<PreviewImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const result = reader.result as string;
      const dimensions = await getResizedImageDimensions(result);
      resolve({
        image: result,
        ...dimensions,
      });
    };
    reader.onerror = (_error) => resolve(defaultPlaceholder());
    reader.readAsDataURL(file);
  });
}

// Generate preview (base64 string) from URL, can be used as src for <img>
export async function generatePreviewFromUrl(url: string): Promise<PreviewImage> {
  return new Promise(async (resolve, reject) => {
    let response: Response;
    try {
      response = await fetch(url);
    } catch (_error) {
      // Could not fetch image, most likely an incorrect URL
      // or CORS is preventing us from fetching the image
      resolve(defaultPlaceholder());
    }

    if (response.ok) {
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onload = async () => {
        const result = reader.result as string;
        const dimensions = await getResizedImageDimensions(result);
        resolve({
          image: result,
          ...dimensions,
        });
      };
      reader.onerror = (_error) => resolve(defaultPlaceholder());
      reader.readAsDataURL(blob);
    } else {
      // Could not fetch image, most likely an incorrect URL
      // or CORS is preventing us from fetching the image
      resolve(defaultPlaceholder());
    }
  });
}

function defaultPlaceholder(): PreviewImage {
  return {
    image: originalPlaceholder,
    width: 614,
    height: 408,
  };
}

async function getResizedImageDimensions(dataUri: string, maxArea = 0.25 * 1000000): Promise<{ width: number; height: number }> {
  return new Promise((resolve, _reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      let { width, height } = img;
      const area = width * height;
      if (area > maxArea) {
        const ratio = Math.sqrt(maxArea / area);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      resolve({ width, height });
    };
    img.onerror = (_error) => resolve({ width: 0, height: 0 });
    img.src = dataUri;
  });
}

// Create a download link and click it
// export function downloadURI(uri: string, name: string): void {
//   const link: HTMLAnchorElement = document.createElement("a");
//   link.download = name;
//   link.href = uri;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// }

export async function downloadURI(uri: string, name: string): Promise<void> {
  const response = await fetch(uri);
  const blob = await response.blob();
  const blobURL = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = blobURL;
  link.download = name;
  link.target = "_blank";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobURL); // Free up memory
}

export async function fileFromUri(uri: string, name: string): Promise<File> {
  let response = await fetch(uri);
  let data = await response.blob();
  return new Promise((resolve, reject) => {
    let metadata = {
      type: "image/png",
    };
    const file = new File([data], name, metadata);
    resolve(file);
  });
}

// native mobile share of the file
export function shareBlob(blob: any, name: string) {
  const file: File = new File([blob], name, {
    type: blob.type,
  });
  navigator.share({
    files: [file],
  });
}

export function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  downloadURI(url, name);
}

export function urlToFile(url: string, filename: string, mimeType: string): Promise<File> {
  return fetch(url)
    .then((res) => res.arrayBuffer())
    .then((buf) => new File([buf], filename, { type: mimeType }));
}

export function urlToBlob(url: string): Promise<Blob> {
  return fetch(url).then((res) => res.blob());
}

export function releaseCanvas(canvas: HTMLCanvasElement | undefined): void {
  if (!canvas) {
    return;
  }
  canvas.width = 0;
  canvas.height = 0;
  const ctx = canvas.getContext("2d");
  ctx && ctx.clearRect(0, 0, 1, 1);
}

export function fetchImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject();
    img.src = url;
  });
}

export function calculateCrop(image, size, clipPosition = "left-top") {
  const width = size.width;
  const height = size.height;
  const aspectRatio = width / height;

  let newWidth;
  let newHeight;

  const imageRatio = image.width / image.height;

  if (aspectRatio >= imageRatio) {
    newWidth = image.width;
    newHeight = image.width / aspectRatio;
  } else {
    newWidth = image.height * aspectRatio;
    newHeight = image.height;
  }

  let x = 0;
  let y = 0;
  if (clipPosition === "left-top") {
    x = 0;
    y = 0;
  } else if (clipPosition === "left-middle") {
    x = 0;
    y = (image.height - newHeight) / 2;
  } else if (clipPosition === "left-bottom") {
    x = 0;
    y = image.height - newHeight;
  } else if (clipPosition === "center-top") {
    x = (image.width - newWidth) / 2;
    y = 0;
  } else if (clipPosition === "center-middle") {
    x = (image.width - newWidth) / 2;
    y = (image.height - newHeight) / 2;
  } else if (clipPosition === "center-bottom") {
    x = (image.width - newWidth) / 2;
    y = image.height - newHeight;
  } else if (clipPosition === "right-top") {
    x = image.width - newWidth;
    y = 0;
  } else if (clipPosition === "right-middle") {
    x = image.width - newWidth;
    y = (image.height - newHeight) / 2;
  } else if (clipPosition === "right-bottom") {
    x = image.width - newWidth;
    y = image.height - newHeight;
  } else if (clipPosition === "scale") {
    x = 0;
    y = 0;
    newWidth = width;
    newHeight = height;
  } else {
    console.error(new Error("Unknown clip position property - " + clipPosition));
  }

  return {
    cropX: x,
    cropY: y,
    cropWidth: newWidth,
    cropHeight: newHeight,
  };
}
